/**
 * MarketingGbpPage — Google Bedrijfsprofiel van Kleda en Cinco: wachtrij met
 * goedkeuring (antwoorden op reviews, posts, foto's, profielcorrecties),
 * gepubliceerd, geskipt, mislukt, profiel (snapshot + drift) en cijfers
 * (Performance API). Uitvoering zit in GitHub Actions (gbp-auto.yml); dit is
 * het toezichtscherm (plan 2026-09-06-gbp-autonoom-beheer.md, stap 7).
 */
import { useEffect, useState } from 'react';
import { GBP_BRANDS, GBP_SOORTEN, useGbpStore, type GbpActie, type GbpActieNaam, type GbpBrand, type GbpSoort, type GbpStatus } from '../../stores/gbp-store';
import { MarketingNav, MarketingPageHeader, SectionCard, StatTile, formatDate, formatDateTime } from './shared';

type Tab = 'wachtrij' | 'goedgekeurd' | 'gepubliceerd' | 'geskipt' | 'mislukt' | 'profiel' | 'cijfers';

const TAB_STATUS: Record<Exclude<Tab, 'profiel' | 'cijfers'>, GbpStatus> = {
  wachtrij: 'concept',
  goedgekeurd: 'goedgekeurd',
  gepubliceerd: 'gepubliceerd',
  geskipt: 'geskipt',
  mislukt: 'mislukt',
};

const STATUS_KLEUR: Record<string, string> = {
  concept: 'bg-amber-50 text-amber-700',
  goedgekeurd: 'bg-blue-50 text-blue-700',
  gepubliceerd: 'bg-green-50 text-green-700',
  geskipt: 'bg-gray-100 text-gray-600',
  afgekeurd: 'bg-red-50 text-red-700',
  mislukt: 'bg-red-50 text-red-700',
};

const SOORT_LABEL: Record<GbpSoort, string> = {
  review_reply: 'antwoord op review',
  post: 'post',
  foto: 'foto',
  profiel_update: 'profielcorrectie',
  qna_answer: 'Q&A-antwoord',
};

const SOORT_KLEUR: Record<GbpSoort, string> = {
  review_reply: 'bg-pink-50 text-pink-700',
  post: 'bg-sky-50 text-sky-700',
  foto: 'bg-teal-50 text-teal-700',
  profiel_update: 'bg-orange-50 text-orange-700',
  qna_answer: 'bg-purple-50 text-purple-700',
};

const MERK_KLEUR: Record<GbpBrand, string> = {
  kleda: 'bg-emerald-50 text-emerald-700',
  cinco: 'bg-indigo-50 text-indigo-700',
};

const METRIC_LABEL: Record<string, string> = {
  BUSINESS_IMPRESSIONS_DESKTOP_MAPS: 'Maps desktop',
  BUSINESS_IMPRESSIONS_DESKTOP_SEARCH: 'Search desktop',
  BUSINESS_IMPRESSIONS_MOBILE_MAPS: 'Maps mobiel',
  BUSINESS_IMPRESSIONS_MOBILE_SEARCH: 'Search mobiel',
  CALL_CLICKS: 'Bel-klikken',
  WEBSITE_CLICKS: 'Website-klikken',
  BUSINESS_DIRECTION_REQUESTS: 'Routeaanvragen',
  BUSINESS_CONVERSATIONS: 'Berichten',
  BUSINESS_BOOKINGS: 'Boekingen',
};

function Badge({ tekst, kleur }: { tekst: string; kleur?: string }) {
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${kleur ?? 'bg-slate-100 text-slate-600'}`}>{tekst}</span>;
}

function Sterren({ n }: { n: number }) {
  return <span className={`text-sm ${n <= 2 ? 'text-red-600' : n === 3 ? 'text-amber-600' : 'text-green-600'}`}>{'★'.repeat(n)}{'☆'.repeat(Math.max(0, 5 - n))}</span>;
}

function impressies(m: Record<string, number>): number {
  return Object.entries(m).filter(([k]) => k.startsWith('BUSINESS_IMPRESSIONS')).reduce((s, [, v]) => s + v, 0);
}

function delta(nu: number, vorig: number): string {
  if (!vorig) return '';
  const p = Math.round(((nu - vorig) / vorig) * 100);
  return ` (${p >= 0 ? '+' : ''}${p}%)`;
}

function kort(v: unknown): string {
  if (v == null) return '–';
  const s = typeof v === 'string' ? v : JSON.stringify(v);
  return s.length > 220 ? s.slice(0, 217) + '…' : s;
}

function ActieRij({ actie, onAct }: { actie: GbpActie; onAct: (a: GbpActieNaam, payload?: { preview?: string; payload?: Record<string, unknown>; reden?: string }) => Promise<void> }) {
  const [tekst, setTekst] = useState(actie.preview ?? '');
  const [cta, setCta] = useState(String(((actie.payload.callToAction as { url?: string } | undefined)?.url) ?? ''));
  const [reden, setReden] = useState('');
  const [skipOpen, setSkipOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [fout, setFout] = useState<string | null>(null);

  const d = actie.bron_detail ?? {};
  const bewerkbaar = actie.soort === 'review_reply' || actie.soort === 'post';
  const gewijzigd = bewerkbaar && (tekst !== (actie.preview ?? '') || (actie.soort === 'post' && cta !== String(((actie.payload.callToAction as { url?: string } | undefined)?.url) ?? '')));
  const isWachtrij = actie.status === 'concept';
  const beeld = d.beeld_url ?? ((actie.payload.media as Array<{ sourceUrl?: string }> | undefined)?.[0]?.sourceUrl) ?? (actie.payload.sourceUrl as string | undefined);

  async function doe(a: GbpActieNaam, payload?: { preview?: string; payload?: Record<string, unknown>; reden?: string }) {
    setBusy(a);
    setFout(null);
    try {
      await onAct(a, payload);
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Mislukt');
    } finally {
      setBusy(null);
    }
  }

  async function opslaan() {
    await doe('edit', { preview: tekst, ...(actie.soort === 'post' ? { payload: { cta_url: cta } } : {}) });
  }

  async function goedkeuren() {
    if (gewijzigd) await opslaan();
    await doe('approve');
  }

  return (
    <div className="flex gap-4 py-4 border-b border-gray-100 last:border-0">
      {(actie.soort === 'post' || actie.soort === 'foto') && (
        <div className="shrink-0 w-[135px]">
          {beeld ? (
            <a href={beeld} target="_blank" rel="noreferrer" title="Open beeld">
              <img src={beeld} alt="" className="w-[135px] h-[135px] object-cover rounded-lg bg-gray-100 hover:opacity-90" />
            </a>
          ) : (
            <div className="w-[135px] h-[135px] rounded-lg bg-gray-100 flex items-center justify-center text-xs text-gray-400">geen beeld</div>
          )}
        </div>
      )}
      <div className="flex-1 min-w-0 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge tekst={actie.status} kleur={STATUS_KLEUR[actie.status]} />
          <Badge tekst={actie.brand} kleur={MERK_KLEUR[actie.brand]} />
          <Badge tekst={SOORT_LABEL[actie.soort] ?? actie.soort} kleur={SOORT_KLEUR[actie.soort]} />
          {actie.soort === 'review_reply' && typeof d.sterren === 'number' && <Sterren n={d.sterren} />}
          {d.toon && <Badge tekst={d.toon} kleur="bg-gray-100 text-gray-600" />}
          {d.category && <Badge tekst={d.category} kleur="bg-gray-100 text-gray-600" />}
          {d.opnieuw && <Badge tekst="opnieuw: week weer af" kleur="bg-amber-50 text-amber-700" />}
          {actie.bewerkt_door && <Badge tekst={`bewerkt door ${actie.bewerkt_door}`} kleur="bg-yellow-50 text-yellow-700" />}
          <span className="text-xs text-gray-400">{formatDateTime(actie.created_at)}</span>
        </div>

        {actie.soort === 'review_reply' && (
          <div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
            <p className="text-xs text-gray-500 mb-1">
              {d.auteur ?? 'anoniem'} · {d.datum ? formatDate(d.datum) : '–'}
            </p>
            <p className="whitespace-pre-line">{d.tekst?.trim() ? d.tekst : <span className="text-gray-400">(review zonder tekst)</span>}</p>
          </div>
        )}
        {(actie.soort === 'post' || actie.soort === 'foto') && d.bron_omschrijving && <p className="text-xs text-gray-500">Bron: {d.bron_omschrijving}{d.waarom ? ` · ${d.waarom}` : ''}</p>}
        {actie.soort === 'profiel_update' && (
          <div className="text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2 space-y-1">
            <p className="text-xs text-gray-500">Veld: {d.veld}</p>
            <p><span className="text-xs text-gray-400 mr-1">live</span>{kort(d.oud)}</p>
            <p><span className="text-xs text-gray-400 mr-1">canon</span>{kort(d.nieuw)}</p>
          </div>
        )}

        {bewerkbaar ? (
          <>
            <textarea value={tekst} onChange={(e) => setTekst(e.target.value)} rows={actie.soort === 'post' ? 4 : 3} disabled={!isWachtrij && actie.status !== 'goedgekeurd'} className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 disabled:bg-gray-50" />
            {actie.soort === 'post' && (
              <input value={cta} onChange={(e) => setCta(e.target.value)} disabled={!isWachtrij && actie.status !== 'goedgekeurd'} className="w-full text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 disabled:bg-gray-50" placeholder="CTA-URL (Meer informatie)" />
            )}
            <p className="text-[11px] text-gray-400">{tekst.length} tekens{actie.soort === 'review_reply' ? ' · 120-450' : ' · 150-300, max 1500'}</p>
          </>
        ) : (
          actie.preview && actie.soort !== 'profiel_update' && <p className="text-sm text-gray-700">{actie.preview}</p>
        )}

        {actie.fout && <p className="text-xs text-red-600">{actie.fout} ({actie.pogingen}/3)</p>}
        {d.fout && <p className="text-xs text-red-600">{d.fout}</p>}
        {actie.beoordeel_reden && <p className="text-xs text-gray-500">Reden: {actie.beoordeel_reden} ({actie.beoordeeld_door})</p>}
        {actie.preview_response?.dry_run && (
          <p className="text-[11px] text-gray-400">dry-run {actie.preview_response.ok ? 'ok' : 'FOUT'}: {JSON.stringify(actie.preview_response.checks)}</p>
        )}

        {actie.status === 'gepubliceerd' && (
          <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
            {actie.extern_url && (
              <a href={actie.extern_url} target="_blank" rel="noreferrer" className="text-indigo-600 hover:underline">Bekijk op Google</a>
            )}
            <span>{formatDateTime(actie.gepubliceerd_op)}</span>
            <Badge tekst={actie.actor_bron === 'brein' ? 'autonoom' : 'goedgekeurd door mens'} kleur={actie.actor_bron === 'brein' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'} />
          </div>
        )}

        {fout && <p className="text-xs text-red-600">{fout}</p>}

        {isWachtrij && (
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <button onClick={goedkeuren} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50">
              {busy === 'approve' ? 'Bezig…' : 'Goedkeuren'}
            </button>
            {gewijzigd && (
              <button onClick={opslaan} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">Opslaan</button>
            )}
            {!skipOpen ? (
              <button onClick={() => setSkipOpen(true)} disabled={busy !== null} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 hover:bg-gray-50">Skippen</button>
            ) : (
              <div className="flex items-center gap-2">
                <input value={reden} onChange={(e) => setReden(e.target.value)} placeholder="Waarom niet? (verplicht)" className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 w-64" />
                <button onClick={() => doe('skip', { reden })} disabled={busy !== null || reden.trim().length < 3} className="px-3 py-1.5 text-sm rounded-lg bg-gray-800 text-white disabled:opacity-40">Bevestig skip</button>
                <button onClick={() => setSkipOpen(false)} className="text-xs text-gray-500">annuleer</button>
              </div>
            )}
          </div>
        )}
        {actie.status === 'goedgekeurd' && (
          <div className="flex gap-2 pt-1">
            {gewijzigd && <button onClick={opslaan} disabled={busy !== null} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">Opslaan</button>}
            <button onClick={() => doe('requeue')} disabled={busy !== null} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">Terug naar wachtrij</button>
            {!skipOpen ? (
              <button onClick={() => setSkipOpen(true)} disabled={busy !== null} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">Toch skippen</button>
            ) : (
              <div className="flex items-center gap-2">
                <input value={reden} onChange={(e) => setReden(e.target.value)} placeholder="Waarom niet? (verplicht)" className="text-sm border border-gray-200 rounded-lg px-3 py-1.5 w-64" />
                <button onClick={() => doe('skip', { reden })} disabled={busy !== null || reden.trim().length < 3} className="px-3 py-1.5 text-xs rounded-lg bg-gray-800 text-white disabled:opacity-40">Bevestig</button>
              </div>
            )}
          </div>
        )}
        {(actie.status === 'geskipt' || actie.status === 'mislukt' || actie.status === 'afgekeurd') && (
          <div className="flex gap-2 pt-1">
            <button onClick={() => doe('approve')} disabled={busy !== null || (bewerkbaar && !tekst.trim())} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50">Toch goedkeuren</button>
            <button onClick={() => doe('requeue')} disabled={busy !== null} className="px-3 py-1.5 text-xs rounded-lg border border-gray-200 hover:bg-gray-50">Terug naar wachtrij</button>
          </div>
        )}
      </div>
    </div>
  );
}

export function MarketingGbpPage() {
  const { acties, stats, soortFilter, merkFilter, isLoading, error, fetchActies, act, fetchStats } = useGbpStore();
  const [tab, setTab] = useState<Tab>('wachtrij');

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  useEffect(() => {
    if (tab !== 'profiel' && tab !== 'cijfers') fetchActies(TAB_STATUS[tab]);
  }, [tab, fetchActies]);

  const lijstTab = tab !== 'profiel' && tab !== 'cijfers';
  const wachtrijN = Object.values(stats?.wachtrij ?? {}).reduce((s, per) => s + (per.concept ?? 0), 0);
  const goedgekeurdN = Object.values(stats?.wachtrij ?? {}).reduce((s, per) => s + (per.goedgekeurd ?? 0), 0);
  const misluktN = Object.values(stats?.wachtrij ?? {}).reduce((s, per) => s + (per.mislukt ?? 0), 0);

  const tabs: Array<{ key: Tab; label: string; n?: number }> = [
    { key: 'wachtrij', label: 'Wachtrij', n: wachtrijN },
    { key: 'goedgekeurd', label: 'Goedgekeurd', n: goedgekeurdN },
    { key: 'gepubliceerd', label: 'Gepubliceerd' },
    { key: 'geskipt', label: 'Geskipt' },
    { key: 'mislukt', label: 'Mislukt', n: misluktN },
    { key: 'profiel', label: 'Profiel' },
    { key: 'cijfers', label: 'Cijfers' },
  ];

  // Gegroepeerd per soort in de wachtrij (plan: "wachtrij per soort gegroepeerd").
  const groepen = GBP_SOORTEN.map((s) => ({ soort: s, items: acties.filter((a) => a.soort === s) })).filter((g) => g.items.length > 0);

  return (
    <div className="flex-1 overflow-y-auto">
      <MarketingPageHeader title="Google Bedrijfsprofiel" subtitle="Kleda en Cinco op Google: antwoorden op reviews, posts, foto's en profielcorrecties. Concepten komen elke ochtend; goedkeuren hier, de publisher doet de rest." />
      <MarketingNav />

      <div className="p-6 space-y-6">
        {error && <div className="bg-red-50 text-red-700 text-sm rounded-lg px-4 py-3">{error}</div>}

        <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
          {GBP_BRANDS.map((b) => {
            const p = stats?.performance[b];
            const nu = p ? impressies(p.d7) : 0;
            const vorig = p ? impressies(p.vorige7) : 0;
            return <StatTile key={b} label={`Impressies 7d · ${b}`} value={p?.laatste_dag ? nu.toLocaleString('nl-NL') : '–'} sub={p?.laatste_dag ? `t/m ${p.laatste_dag}${delta(nu, vorig)}` : 'Performance API nog uit'} />;
          })}
          {GBP_BRANDS.map((b) => {
            const g = stats?.antwoordgraad[b];
            return <StatTile key={b} label={`Antwoordgraad 30d · ${b}`} value={g ? `${g.pct}%` : '–'} sub={g ? `${g.beantwoord} van ${g.totaal}${g.open_48u ? ` · ${g.open_48u} > 48u open` : ''}` : ''} />;
          })}
          <StatTile label="Wachtrij" value={wachtrijN} sub={Object.entries(stats?.wachtrij ?? {}).map(([s, per]) => `${per.concept ?? 0} ${SOORT_LABEL[s as GbpSoort] ?? s}`).join(' · ') || 'leeg'} />
          <StatTile label="% autonoom (MTD)" value={`${stats?.autonoom.pct ?? 0}%`} sub={`${stats?.autonoom.mtd_brein ?? 0} van ${stats?.autonoom.mtd_gepubliceerd ?? 0} zonder mens`} />
        </div>

        <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
          {tabs.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t.key ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
              {t.n ? <span className="ml-1 text-xs bg-gray-100 text-gray-600 rounded-full px-1.5">{t.n}</span> : null}
            </button>
          ))}
        </div>

        {lijstTab && (
          <div className="flex flex-wrap items-center gap-1">
            <span className="text-xs text-gray-400 mr-1">Merk:</span>
            <button onClick={() => fetchActies(undefined, undefined, null)} className={`px-2 py-0.5 rounded-full text-xs ${merkFilter === null ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>beide</button>
            {GBP_BRANDS.map((b) => (
              <button key={b} onClick={() => fetchActies(undefined, undefined, b)} className={`px-2 py-0.5 rounded-full text-xs ${merkFilter === b ? 'bg-gray-800 text-white' : `${MERK_KLEUR[b]} hover:opacity-80`}`}>{b}</button>
            ))}
            <span className="text-xs text-gray-400 ml-3 mr-1">Soort:</span>
            <button onClick={() => fetchActies(undefined, null)} className={`px-2 py-0.5 rounded-full text-xs ${soortFilter === null ? 'bg-gray-800 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>alle</button>
            {GBP_SOORTEN.map((s) => (
              <button key={s} onClick={() => fetchActies(undefined, s)} className={`px-2 py-0.5 rounded-full text-xs ${soortFilter === s ? 'bg-gray-800 text-white' : `${SOORT_KLEUR[s]} hover:opacity-80`}`}>{SOORT_LABEL[s]}</button>
            ))}
          </div>
        )}

        {lijstTab && (
          <SectionCard>
            {isLoading && acties.length === 0 && <p className="text-sm text-gray-400">Laden…</p>}
            {!isLoading && acties.length === 0 && (
              <p className="text-sm text-gray-400">{tab === 'wachtrij' ? 'Niets te beoordelen. Nieuwe concepten komen elke ochtend 08:00 (antwoorden) en di/vr 11:00 (posts, foto’s).' : 'Geen acties.'}</p>
            )}
            {groepen.map((g) => (
              <div key={g.soort} className="mb-4 last:mb-0">
                {groepen.length > 1 && <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-2">{SOORT_LABEL[g.soort]} ({g.items.length})</h4>}
                {g.items.map((a) => (
                  <ActieRij key={a.id} actie={a} onAct={(actie, payload) => act(a.id, actie, payload)} />
                ))}
              </div>
            ))}
          </SectionCard>
        )}

        {tab === 'profiel' && (
          <div className="grid md:grid-cols-2 gap-6">
            {GBP_BRANDS.map((b) => {
              const s = stats?.snapshot[b];
              const au = stats?.audit[b];
              return (
                <SectionCard key={b} title={`${b === 'kleda' ? 'Kleda' : 'Cinco Cleaning'} · snapshot ${s?.dag ?? '–'}`}>
                  {!s && <p className="text-sm text-gray-400">Nog geen snapshot (dagelijks 08:00).</p>}
                  {s && (
                    <div className="space-y-3 text-sm">
                      <p><span className="text-xs text-gray-400 mr-1">titel</span>{s.titel}</p>
                      <p><span className="text-xs text-gray-400 mr-1">categorieën</span>{s.categorieen?.primary?.replace('categories/gcid:', '')}{s.categorieen?.additional?.length ? ` + ${s.categorieen.additional.map((c) => c.replace('categories/gcid:', '')).join(', ')}` : ''}</p>
                      <p className="text-gray-600 whitespace-pre-line text-xs">{s.beschrijving}</p>
                      <p className="text-xs text-gray-500">{s.diensten?.length ?? 0} diensten · {stats?.fotos[b] ?? 0} foto's via dit systeem · {stats?.posts30d[b] ?? 0} posts 30d</p>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Afwijking van de canon ({s.drift_canon?.length ?? 0})</p>
                        {(s.drift_canon ?? []).map((dr, i) => (
                          <p key={i} className="text-xs text-gray-600">· {dr.veld}: {kort(dr.live)} → {kort(dr.canon)}</p>
                        ))}
                        {!s.drift_canon?.length && <p className="text-xs text-green-700">Profiel volgt de canon.</p>}
                      </div>
                      <div>
                        <p className="text-xs font-medium text-gray-500">Door Google gewijzigd sinds de vorige snapshot ({s.drift_google?.length ?? 0})</p>
                        {(s.drift_google ?? []).map((dr, i) => (
                          <p key={i} className="text-xs text-amber-700">· {dr.veld}: {kort(dr.oud)} → {kort(dr.nieuw)}</p>
                        ))}
                      </div>
                      {au && (
                        <div className="pt-2 border-t border-gray-100">
                          <p className="text-xs font-medium text-gray-500">Audit {formatDate(au.run_at)}: score {Math.round(au.score)}/100</p>
                          {Object.entries(au.detail.onderdelen ?? {}).map(([k, o]) => (
                            <p key={k} className="text-xs text-gray-600">· {k}: {o.punten}/{o.max}{o.toelichting ? ` · ${o.toelichting}` : ''}</p>
                          ))}
                          {(au.detail.acties ?? []).map((t, i) => <p key={i} className="text-xs text-indigo-700">→ {t}</p>)}
                        </div>
                      )}
                    </div>
                  )}
                </SectionCard>
              );
            })}
          </div>
        )}

        {tab === 'cijfers' && (
          <div className="grid md:grid-cols-2 gap-6">
            {GBP_BRANDS.map((b) => {
              const p = stats?.performance[b];
              const metrics = Object.keys(METRIC_LABEL);
              return (
                <SectionCard key={b} title={`${b === 'kleda' ? 'Kleda' : 'Cinco Cleaning'} · Performance API${p?.laatste_dag ? ` t/m ${p.laatste_dag}` : ''}`}>
                  {!p?.laatste_dag && <p className="text-sm text-gray-400">Nog geen data. De Performance API moet aan staan in het Cloud-project; daarna vult de dagelijkse collector dit (Google levert met 2-3 dagen vertraging).</p>}
                  {p?.laatste_dag && (
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-gray-400">
                          <th className="text-left font-normal">metric</th>
                          <th className="text-right font-normal">7d</th>
                          <th className="text-right font-normal">vorige 7d</th>
                          <th className="text-right font-normal">28d</th>
                        </tr>
                      </thead>
                      <tbody>
                        {metrics.map((m) => (
                          <tr key={m} className="border-t border-gray-100">
                            <td className="py-1 text-gray-700">{METRIC_LABEL[m]}</td>
                            <td className="py-1 text-right">{(p.d7[m] ?? 0).toLocaleString('nl-NL')}</td>
                            <td className="py-1 text-right text-gray-500">{(p.vorige7[m] ?? 0).toLocaleString('nl-NL')}</td>
                            <td className="py-1 text-right">{(p.d28[m] ?? 0).toLocaleString('nl-NL')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </SectionCard>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
